/**
 * Service-worker update state machine (D-024, D-044).
 *
 * Pure: no clock, no service worker, no storage. It is a reducer over explicit
 * events so that every rule D-044 states can be asserted directly against it
 * rather than inferred from the behaviour of a live registration.
 *
 *   idle ──(update detected)──► deferred      when a review is active
 *                            └─► promptable   when none is
 *
 *   deferred ──(review finalized | abandoned)──► promptable
 *   promptable ──(review started)──► deferred
 *   promptable ──(user asks)──► activating ──(draft save resolved)──► applying
 *                                          └─(draft save failed)──► activationFailed
 *
 * The three rules that carry the risk:
 *
 *  1. `applying` is the ONLY status at which the waiting worker may be
 *     activated, and the ONLY route into it is `activationSaveConfirmed` from
 *     `activating`. Reaching `activating` requires `activationRequested` from
 *     `promptable` or `activationFailed` — never from `deferred`. So an active
 *     review cannot reach activation by any sequence of events.
 *  2. `draftAutosaved` is deliberately an identity transition. Routine autosave
 *     is not permission to activate (D-044). It exists as an event so that the
 *     rule is proven by a test rather than by the absence of code.
 *  3. A failed draft save does not activate. It parks in `activationFailed`
 *     with the reason, which the UI surfaces; the user may ask again.
 */

export type UpdateStatus =
  /** No update is waiting. */
  | 'idle'
  /** An update is waiting but an active weekly review blocks offering it. */
  | 'deferred'
  /** An update is waiting and may be offered to the user. */
  | 'promptable'
  /** The user asked; the draft is being force-saved. */
  | 'activating'
  /** The draft save resolved; the waiting worker may now be activated. */
  | 'applying'
  /** The draft save failed. The update was not applied. */
  | 'activationFailed'

export interface UpdateState {
  readonly status: UpdateStatus
  /** Tracked across every status so a review started mid-flow is not lost. */
  readonly reviewActive: boolean
  /** Set only in `activationFailed`. */
  readonly failureReason: string | null
}

export type UpdateEvent =
  | { readonly type: 'updateDetected' }
  | { readonly type: 'reviewStarted' }
  | { readonly type: 'reviewFinalized' }
  | { readonly type: 'reviewAbandoned' }
  | { readonly type: 'draftAutosaved' }
  | { readonly type: 'activationRequested' }
  | { readonly type: 'activationSaveConfirmed' }
  | { readonly type: 'activationSaveFailed'; readonly reason: string }

export const INITIAL_UPDATE_STATE: UpdateState = {
  status: 'idle',
  reviewActive: false,
  failureReason: null,
}

/** The single predicate the UI uses to decide whether to offer the action. */
export function isUpdateOfferable(state: UpdateState): boolean {
  return state.status === 'promptable' || state.status === 'activationFailed'
}

/**
 * The single predicate the client uses to decide whether it may activate the
 * waiting worker. Nothing else may call `skipWaiting`; the layering guard
 * enforces that only `serviceWorkerClient.ts` mentions it at all.
 */
export function mayActivateWaitingWorker(state: UpdateState): boolean {
  return state.status === 'applying'
}

/*
 * One small function per transition rather than one large switch. This is the
 * function whose correctness the whole guarantee rests on, so it is kept below
 * the complexity threshold by structure rather than by suppressing the warning
 * (D-056).
 */

function onUpdateDetected(state: UpdateState): UpdateState {
  // A repeated notification for an update already known changes nothing; in
  // particular it must never pull an in-flight activation backwards.
  if (state.status !== 'idle') return state
  return { ...state, status: state.reviewActive ? 'deferred' : 'promptable' }
}

function onReviewStarted(state: UpdateState): UpdateState {
  /* `activating` is included deliberately. The draft save runs async, so a
     review can begin between the user tapping Update and the save resolving;
     letting the activation finish would reload a review that had just started.
     Aborting to `deferred` here means the confirmation, when it arrives, is
     ignored — the update simply waits again. */
  const aborts = state.status === 'promptable' || state.status === 'activating'
  return { ...state, status: aborts ? 'deferred' : state.status, reviewActive: true }
}

function onReviewEnded(state: UpdateState): UpdateState {
  const status = state.status === 'deferred' ? 'promptable' : state.status
  return { ...state, status, reviewActive: false }
}

function onActivationRequested(state: UpdateState): UpdateState {
  if (state.status !== 'promptable' && state.status !== 'activationFailed') return state
  // `promptable` already implies no active review, but a retry from
  // `activationFailed` does not.
  if (state.reviewActive) return state
  return { ...state, status: 'activating', failureReason: null }
}

function onActivationSaveConfirmed(state: UpdateState): UpdateState {
  /* No `reviewActive` check here, deliberately. `reviewStarted` already moves
     an in-flight activation to `deferred`, so this guard would be unreachable —
     and an unreachable guard cannot be shown to fail, which makes it a
     liability rather than defence in depth (D-056). If that transition is ever
     removed, the exhaustive reachability test in updateMachine.test.ts fails,
     which is the real safety net. */
  if (state.status !== 'activating') return state
  return { ...state, status: 'applying' }
}

function onActivationSaveFailed(state: UpdateState, reason: string): UpdateState {
  if (state.status !== 'activating') return state
  return { ...state, status: 'activationFailed', failureReason: reason }
}

export function nextUpdateState(state: UpdateState, event: UpdateEvent): UpdateState {
  switch (event.type) {
    case 'updateDetected':
      return onUpdateDetected(state)
    case 'reviewStarted':
      return onReviewStarted(state)
    case 'reviewFinalized':
    case 'reviewAbandoned':
      return onReviewEnded(state)
    case 'draftAutosaved':
      // Identity, on purpose. See rule 2 above.
      return state
    case 'activationRequested':
      return onActivationRequested(state)
    case 'activationSaveConfirmed':
      return onActivationSaveConfirmed(state)
    case 'activationSaveFailed':
      return onActivationSaveFailed(state, event.reason)
    default: {
      // Exhaustiveness: adding an event without handling it fails the typecheck.
      const unreachable: never = event
      return unreachable
    }
  }
}
